import React from "react";
import { Link } from "react-router-dom";
import { MARKETING_ZONES, ZONE_DETAILS } from "../lib/api";
import useSEO from "../lib/useSEO";
import { useT } from "../lib/i18n";
import { breadcrumbLd, faqLd, ZONE_FAQ, zonePlaceLd } from "../lib/jsonld";

export default function Zones() {
  const { t } = useT();
  const jsonLd = React.useMemo(() => [
    breadcrumbLd([{ name: "Home", url: "/" }, { name: "Zones", url: "/zones" }]),
    faqLd(ZONE_FAQ),
    ...Object.entries(ZONE_DETAILS).map(([name, z]) => zonePlaceLd(name, z)),
  ], []);
  useSEO({
    title: "Dubai Business Zones — Warehouse & Industrial Property Guide | WarehousesUAE",
    description: "Compare Dubai's industrial business zones — JAFZA, DIP, Al Quoz, Ras Al Khor, Dubai South, DIC, Al Warsan, NIP, Sonapur, Jebel Ali Industrial. Find the right zone for your operation.",
    canonical: window.location.origin + "/zones",
    image: window.location.origin + "/og-default.png",
    jsonLd,
  });

  const entries = MARKETING_ZONES.map((name) => [name, ZONE_DETAILS[name]]);

  return (
    <div data-testid="zones-page" className="bg-zinc-50 min-h-screen">
      <div className="bg-zinc-950 text-white py-16 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-30"></div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="font-mono-tech text-[10px] uppercase text-[#FFCC00] mb-3">{t("zones.hero_kicker")}</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95]">
            {t("zones.hero_title_a")}<br />{t("zones.hero_title_b")}
          </h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            {t("zones.hero_sub")}
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 grid md:grid-cols-2 gap-6">
        {entries.map(([name, z], i) => (
          <Link
            key={name}
            to={`/zones/${z.slug}`}
            data-testid={`zone-detail-${name}`}
            className="group bg-white border border-zinc-200 hover:border-zinc-900 transition-colors"
          >
            <div className="aspect-[16/9] overflow-hidden bg-zinc-100">
              <img src={z.img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500">ZONE / {String(i + 1).padStart(2, "0")}</div>
                <div className="font-mono-tech text-[10px] uppercase text-[#031833] group-hover:translate-x-1 transition-transform">{t("zones.view_zone")}</div>
              </div>
              <h2 className="font-display text-3xl tracking-tighter mb-2">{name}</h2>
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">{z.description}</p>
              <div className="flex flex-wrap gap-2">
                {z.tags.map((t) => (
                  <span key={t} className="border border-zinc-300 px-2 py-0.5 font-mono-tech text-[10px] uppercase">{t}</span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
